import { Authenticated, Refine } from "@refinedev/core";
import { DevtoolsPanel, DevtoolsProvider } from "@refinedev/devtools";
import { RefineKbar, RefineKbarProvider } from "@refinedev/kbar";
import {
  ErrorComponent,
  ThemedLayout,
  ThemedSider,
  useNotificationProvider,
} from "@refinedev/antd";
import "@refinedev/antd/dist/reset.css";
import "./overrides.css";
import routerProvider, {
  CatchAllNavigate,
  DocumentTitleHandler,
  NavigateToResource,
  UnsavedChangesNotifier,
} from "@refinedev/react-router";
import { ApiOutlined, CodeOutlined, FileSearchOutlined, FormOutlined, HistoryOutlined, ProjectOutlined, SettingOutlined } from "@ant-design/icons";
import { App as AntdApp } from "antd";
import { BrowserRouter, Outlet, Route, Routes } from "react-router";
import { GoogleOAuthProvider } from "@react-oauth/google";
import { Header, Title } from "./components";
import { ColorModeContextProvider } from "./contexts/color-mode";
import { createDataProvider } from "./api/dataProvider";
import { authProvider } from "./providers/authProvider";
import { CliAuth } from "./pages/cli-auth";
import { Login } from "./pages/login";
import { WpCallback } from "./pages/wp-callback";
import { TokenList } from "./pages/tokens/list";
import { ProjectList } from "./pages/projects/list";
import { DeploymentList } from "./pages/deployments/list";
import { SnippetList } from "./pages/snippets/list";
import { SeoList } from "./pages/seo/list";
import { AcfList } from "./pages/acf/list";
import { Settings } from "./pages/settings";

const dataProvider = createDataProvider();

function App() {
  return (
    <GoogleOAuthProvider clientId={import.meta.env.VITE_GOOGLE_CLIENT_ID ?? ""}>
      <BrowserRouter>
        <RefineKbarProvider>
          <ColorModeContextProvider>
            <AntdApp>
              <DevtoolsProvider>
                <Refine
                  dataProvider={dataProvider}
                  notificationProvider={useNotificationProvider}
                  routerProvider={routerProvider}
                  authProvider={authProvider}
                  resources={[
                    {
                      name: "projects",
                      list: "/projects",
                      meta: { label: "Projects", icon: <ProjectOutlined /> },
                    },
                    {
                      name: "snippets",
                      list: "/snippets",
                      meta: { label: "Snippets", icon: <CodeOutlined /> },
                    },
                    {
                      name: "seo",
                      list: "/seo",
                      meta: { label: "SEO", icon: <FileSearchOutlined /> },
                    },
                    {
                      name: "acf",
                      list: "/acf",
                      meta: { label: "ACF", icon: <FormOutlined /> },
                    },
                    {
                      name: "deployments",
                      list: "/deployments",
                      meta: { label: "Deployments", icon: <HistoryOutlined /> },
                    },
                    {
                      name: "tokens",
                      list: "/tokens",
                      meta: { label: "Tokens", icon: <ApiOutlined /> },
                    },
                    {
                      name: "settings",
                      list: "/settings",
                      meta: { label: "Settings", icon: <SettingOutlined /> },
                    },
                  ]}
                  options={{
                    syncWithLocation: true,
                    warnWhenUnsavedChanges: false,
                    projectId: "A7p6Nd-J6rES8-Px5od5",
                    title: {
                      text: "Loopress"
                    }
                  }}
                >
                  <Routes>
                    <Route
                      element={
                        <Authenticated
                          key="authenticated-inner"
                          fallback={<CatchAllNavigate to="/login" />}
                        >
                          <ThemedLayout
                            Header={Header}
                            Sider={(props) => <ThemedSider {...props} fixed Title={Title} />}
                          >
                            <Outlet />
                          </ThemedLayout>
                        </Authenticated>
                      }
                    >
                      <Route index element={<NavigateToResource resource="projects" />} />
                      <Route path="/tokens" element={<TokenList />} />
                      <Route path="/projects" element={<ProjectList />} />
                      <Route path="/deployments" element={<DeploymentList />} />
                      <Route path="/snippets" element={<SnippetList />} />
                      <Route path="/seo" element={<SeoList />} />
                      <Route path="/acf" element={<AcfList />} />
                      <Route path="/settings" element={<Settings />} />
                      <Route path="*" element={<ErrorComponent />} />
                    </Route>
                    <Route
                      element={
                        <Authenticated
                          key="authenticated-outer"
                          fallback={<Outlet />}
                        >
                          <NavigateToResource />
                        </Authenticated>
                      }
                    >
                      <Route path="/login" element={<Login />} />
                    </Route>
                    <Route path="/cli-auth" element={<CliAuth />} />
                    <Route path="/wp-callback" element={<WpCallback />} />
                  </Routes>

                  <RefineKbar />
                  <UnsavedChangesNotifier />
                  <DocumentTitleHandler />
                </Refine>
                <DevtoolsPanel />
              </DevtoolsProvider>
            </AntdApp>
          </ColorModeContextProvider>
        </RefineKbarProvider>
      </BrowserRouter>
    </GoogleOAuthProvider>
  );
}

export default App;
